import React, { useState } from 'react'

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    wantsCourseInfo: true,
    message: '',
    website_url: '', // Mandatory honeypot
  })

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  })

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ submitting: true, submitted: false, error: null })

    // Basic client validations
    if (!formData.name.trim()) {
      setStatus({ submitting: false, submitted: false, error: 'Venligst indtast dit navn.' })
      return
    }
    if (!formData.phone.trim()) {
      setStatus({ submitting: false, submitted: false, error: 'Venligst indtast dit telefonnummer.' })
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.trim())) {
      setStatus({ submitting: false, submitted: false, error: 'Venligst indtast en gyldig e-mailadresse.' })
      return
    }
    if (!formData.message.trim()) {
      setStatus({ submitting: false, submitted: false, error: 'Venligst beskriv din situation eller dit ønske.' })
      return
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          navn: formData.name,
          telefon: formData.phone,
          email: formData.email,
          oensker_forloebsinfo: formData.wantsCourseInfo ? 'Ja' : 'Nej',
          besked: formData.message,
          website_url: formData.website_url, // Honeypot
        }),
      })

      if (!response.ok) {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen eller kontakt Bo direkte på telefon.')
      }

      setStatus({ submitting: false, submitted: true, error: null })
      setFormData({
        name: '',
        phone: '',
        email: '',
        wantsCourseInfo: true,
        message: '',
        website_url: '',
      })
    } catch (err) {
      setStatus({
        submitting: false,
        submitted: false,
        error: err.message || 'Der opstod en fejl. Du kan også altid ringe eller sende en mail direkte.',
      })
    }
  }

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-sm border border-[#E5E0D8]">
      <div className="mb-5">
        <span className="text-[11px] font-semibold tracking-wider text-[#7c875a] uppercase block mb-1">
          Uforpligtende henvendelse
        </span>
        <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1b3a4b]">
          Start en samtale om et forløb
        </h3>
        <p className="mt-1.5 text-xs sm:text-sm text-[#64748B] leading-relaxed">
          Udfyld formularen for at høre mere om, hvordan et forløb tilpasses dig. Bo svarer personligt.
        </p>
      </div>

      {status.submitted ? (
        <div className="bg-[#FAF8F5] border border-[#7c875a]/30 rounded-xl p-6 text-center space-y-3">
          <div className="w-11 h-11 mx-auto rounded-full bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center text-lg">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="font-serif text-lg sm:text-xl font-bold text-[#1b3a4b]">
            Mange tak for din henvendelse
          </h4>
          <p className="text-xs sm:text-sm text-[#475569] max-w-sm mx-auto leading-relaxed">
            Jeg har modtaget din besked og kontakter dig hurtigst muligt, så vi kan drøfte dine behov og det rette forløb.
          </p>
          <button
            type="button"
            onClick={() => setStatus({ submitting: false, submitted: false, error: null })}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#1b3a4b] font-semibold hover:text-[#7c875a] transition-colors"
          >
            <span>Send en ny besked</span>
            <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Honeypot field (hidden from real users) */}
          <input
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
            className="sr-only absolute -left-[9999px]"
            aria-hidden="true"
          />

          {status.error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2.5">
              <i className="fa-solid fa-circle-exclamation mt-0.5 text-rose-500 text-xs" aria-hidden="true" />
              <span>{status.error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Navn */}
            <div>
              <label htmlFor="form-name" className="block text-xs font-medium text-[#1e293b] mb-1">
                Navn <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                id="form-name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit navn"
                className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-[#E5E0D8] bg-[#FAF8F5]/40 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1b3a4b]/15 focus:border-[#1b3a4b] text-[#1e293b] transition"
              />
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor="form-phone" className="block text-xs font-medium text-[#1e293b] mb-1">
                Telefon <span className="text-rose-500">*</span>
              </label>
              <input
                type="tel"
                id="form-phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 53 12 82 13"
                className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-[#E5E0D8] bg-[#FAF8F5]/40 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1b3a4b]/15 focus:border-[#1b3a4b] text-[#1e293b] transition"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="form-email" className="block text-xs font-medium text-[#1e293b] mb-1">
              Email <span className="text-rose-500">*</span>
            </label>
            <input
              type="email"
              id="form-email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="din-mail@eksempel.dk"
              className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-[#E5E0D8] bg-[#FAF8F5]/40 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1b3a4b]/15 focus:border-[#1b3a4b] text-[#1e293b] transition"
            />
          </div>

          {/* Checkbox forløb */}
          <div className="pt-0.5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                name="wantsCourseInfo"
                checked={formData.wantsCourseInfo}
                onChange={handleChange}
                className="mt-0.5 h-3.5 w-3.5 rounded border-[#E5E0D8] text-[#1b3a4b] focus:ring-[#1b3a4b]"
              />
              <span className="text-xs text-[#64748B] leading-snug">
                Jeg ønsker information om et individuelt <strong>forløb</strong>
              </span>
            </label>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="form-message" className="block text-xs font-medium text-[#1e293b] mb-1">
              Kort beskrivelse af din situation <span className="text-rose-500">*</span>
            </label>
            <textarea
              id="form-message"
              name="message"
              rows={3}
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Hvad oplever du i krop, sind eller hverdag..."
              className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-[#E5E0D8] bg-[#FAF8F5]/40 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1b3a4b]/15 focus:border-[#1b3a4b] text-[#1e293b] transition resize-y"
            />
          </div>

          {/* Submit CTA */}
          <div className="pt-1">
            <button
              type="submit"
              disabled={status.submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1b3a4b] hover:bg-[#132835] text-white px-5 py-2.5 rounded-lg text-xs sm:text-sm font-semibold shadow-sm hover:shadow transition-all disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
            >
              {status.submitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-xs" aria-hidden="true" />
                  <span>Sender...</span>
                </>
              ) : (
                <>
                  <span>Send henvendelse</span>
                  <i className="fa-solid fa-arrow-right text-[11px] text-[#7c875a]" aria-hidden="true" />
                </>
              )}
            </button>
            <p className="mt-2 text-[11px] text-[#64748B] text-center">
              100 % fortroligt · Besvares personligt af Bo Steendahl
            </p>
          </div>
        </form>
      )}
    </div>
  )
}
